import {
  averageOfferConversionRate,
  formatOrdersValue,
  formatPercentValue,
  formatRatingValue,
  formatRevenueValue,
  getRatingComparisonValue,
  getTotalRevenueAmount,
} from "@/components/pages/analytics/front-desk/components/overview/overviewKpiMappers";
import type { PartnerHotelOverview } from "@/types/partner-dashboard";
import type {
  OfferConversionResponse,
  OrdersByDayResponse,
  RatingsByStayResponse,
} from "@/types/analytics-overview";
import type { TotalRevenueResponse } from "@/types/analytics-revenue";
import type {
  TenantOffersTableRow,
  TenantRevenueTableRow,
  TenantReviewsTableRow,
  TenantServicesTableRow,
} from "@/types/analytics-tenant";
import type { AdminHotelOverviewRow } from "../types/adminOverview";

import type { MetricCardTrend } from "@/components/pages/analytics/front-desk/components/shared/MetricCard";

const DELTA_SUFFIX = " from previous period";

function percentChange(current: number, previous: number): number {
  if (current === previous) {
    return 0;
  }

  if (previous === 0) {
    return current === 0 ? 0 : 100;
  }

  return ((current - previous) / previous) * 100;
}

function trendFromChange(changePct: number): MetricCardTrend {
  if (changePct === 0) return "neutral";
  return changePct > 0 ? "positive" : "negative";
}

function formatPercentDelta(changePct: number): {
  delta: string;
  trend: MetricCardTrend;
} {
  const sign = changePct > 0 ? "+" : "";
  const formatted = changePct.toLocaleString("en-US", {
    maximumFractionDigits: 1,
  });
  return {
    delta: `${sign}${formatted}%${DELTA_SUFFIX}`,
    trend: trendFromChange(changePct),
  };
}

export function mapHotelToAdminOverviewRow(
  hotel: PartnerHotelOverview,
  metrics: {
    revenueTotal: TotalRevenueResponse | null;
    offerConversion: OfferConversionResponse | null;
    ratingsByStay: RatingsByStayResponse | null;
    ordersByDay: OrdersByDayResponse | null;
  },
): AdminHotelOverviewRow {
  const serviceRevenue = getTotalRevenueAmount(
    metrics.revenueTotal ?? undefined,
  );
  const conversionOfOffers = averageOfferConversionRate(
    metrics.offerConversion?.totals,
  );
  const avgGuestRating = getRatingComparisonValue(
    metrics.ratingsByStay?.totals,
  );
  const ordersForStay = metrics.ordersByDay?.totals.orderCount ?? 0;

  return {
    id: hotel.hotelId,
    hotelBrand: hotel.hotelName,
    hotelLogoId: hotel.hotelLogoId,
    serviceRevenue,
    serviceRevenueFormatted: formatRevenueValue(serviceRevenue),
    conversionOfOffers,
    conversionOfOffersFormatted: formatPercentValue(conversionOfOffers),
    avgGuestRating,
    avgGuestRatingFormatted: formatRatingValue(avgGuestRating),
    ordersForStay,
    ordersForStayFormatted: formatOrdersValue(ordersForStay),
  };
}

export function mapTenantTablesToAdminOverviewRows(input: {
  revenueRows: TenantRevenueTableRow[];
  offersRows: TenantOffersTableRow[];
  reviewsRows: TenantReviewsTableRow[];
  servicesRows: TenantServicesTableRow[];
  hotelLogosById?: Record<string, string | null>;
}): AdminHotelOverviewRow[] {
  const offersByHotelId = new Map(
    input.offersRows.map((row) => [row.hotelId, row]),
  );
  const reviewsByHotelId = new Map(
    input.reviewsRows.map((row) => [row.hotelId, row]),
  );
  const servicesByHotelId = new Map(
    input.servicesRows.map((row) => [row.hotelId, row]),
  );

  return input.revenueRows.map((revenueRow) => {
    const divisor = revenueRow.amountDivisor || 1;
    const serviceRevenue = revenueRow.totalRevenue / divisor;
    const offers = offersByHotelId.get(revenueRow.hotelId);
    const reviews = reviewsByHotelId.get(revenueRow.hotelId);
    const services = servicesByHotelId.get(revenueRow.hotelId);
    const conversionOfOffers = offers?.conversionRate ?? 0;
    const avgGuestRating = reviews?.avgRating ?? 0;
    const ordersForStay = services?.orderCount ?? 0;

    return {
      id: revenueRow.hotelId,
      hotelBrand: revenueRow.hotelName,
      hotelLogoId: input.hotelLogosById?.[revenueRow.hotelId] ?? null,
      serviceRevenue,
      serviceRevenueFormatted: formatRevenueValue(serviceRevenue),
      conversionOfOffers,
      conversionOfOffersFormatted: formatPercentValue(conversionOfOffers),
      avgGuestRating,
      avgGuestRatingFormatted: formatRatingValue(avgGuestRating),
      ordersForStay,
      ordersForStayFormatted: formatOrdersValue(ordersForStay),
    };
  });
}

export function mapTenantTablesToAdminOverviewRowsWithComparison(input: {
  revenueRows: TenantRevenueTableRow[];
  offersRows: TenantOffersTableRow[];
  reviewsRows: TenantReviewsTableRow[];
  servicesRows: TenantServicesTableRow[];

  previousRevenueRows: TenantRevenueTableRow[];
  previousOffersRows: TenantOffersTableRow[];
  previousReviewsRows: TenantReviewsTableRow[];
  previousServicesRows: TenantServicesTableRow[];

  hotelLogosById?: Record<string, string | null>;
}): AdminHotelOverviewRow[] {
  const currentOffersByHotelId = new Map(
    input.offersRows.map((row) => [row.hotelId, row]),
  );
  const currentReviewsByHotelId = new Map(
    input.reviewsRows.map((row) => [row.hotelId, row]),
  );
  const currentServicesByHotelId = new Map(
    input.servicesRows.map((row) => [row.hotelId, row]),
  );

  const prevOffersByHotelId = new Map(
    input.previousOffersRows.map((row) => [row.hotelId, row]),
  );
  const prevReviewsByHotelId = new Map(
    input.previousReviewsRows.map((row) => [row.hotelId, row]),
  );
  const prevServicesByHotelId = new Map(
    input.previousServicesRows.map((row) => [row.hotelId, row]),
  );

  const prevRevenueByHotelId = new Map(
    input.previousRevenueRows.map((row) => [row.hotelId, row]),
  );

  return input.revenueRows.map((revenueRow) => {
    const divisor = revenueRow.amountDivisor || 1;
    const currentServiceRevenue = revenueRow.totalRevenue / divisor;

    const prevRevenueRow = prevRevenueByHotelId.get(revenueRow.hotelId);
    const prevDivisor = prevRevenueRow?.amountDivisor || 1;
    const prevServiceRevenue =
      (prevRevenueRow?.totalRevenue ?? 0) / prevDivisor;

    const currentOffers = currentOffersByHotelId.get(revenueRow.hotelId);
    const prevOffers = prevOffersByHotelId.get(revenueRow.hotelId);

    const currentConversionOfOffers = currentOffers?.conversionRate ?? 0;
    const prevConversionOfOffers = prevOffers?.conversionRate ?? 0;

    const currentReviews = currentReviewsByHotelId.get(revenueRow.hotelId);
    const prevReviews = prevReviewsByHotelId.get(revenueRow.hotelId);

    const currentAvgGuestRating = currentReviews?.avgRating ?? 0;
    const prevAvgGuestRating = prevReviews?.avgRating ?? 0;

    const currentServices = currentServicesByHotelId.get(revenueRow.hotelId);
    const prevServices = prevServicesByHotelId.get(revenueRow.hotelId);

    const currentOrdersForStay = currentServices?.orderCount ?? 0;
    const prevOrdersForStay = prevServices?.orderCount ?? 0;

    const serviceRevenueChangePct = percentChange(
      currentServiceRevenue,
      prevServiceRevenue,
    );
    const conversionChangePct = percentChange(
      currentConversionOfOffers,
      prevConversionOfOffers,
    );
    const ratingChangePct = percentChange(
      currentAvgGuestRating,
      prevAvgGuestRating,
    );
    const ordersChangePct = percentChange(
      currentOrdersForStay,
      prevOrdersForStay,
    );

    const serviceRevenueDelta = formatPercentDelta(serviceRevenueChangePct);
    const conversionDelta = formatPercentDelta(conversionChangePct);
    const ratingDelta = formatPercentDelta(ratingChangePct);
    const ordersDelta = formatPercentDelta(ordersChangePct);

    return {
      id: revenueRow.hotelId,
      hotelBrand: revenueRow.hotelName,
      hotelLogoId: input.hotelLogosById?.[revenueRow.hotelId] ?? null,

      serviceRevenue: currentServiceRevenue,
      serviceRevenueFormatted: formatRevenueValue(currentServiceRevenue),
      serviceRevenueDelta: serviceRevenueDelta.delta,
      serviceRevenueTrend: serviceRevenueDelta.trend,

      conversionOfOffers: currentConversionOfOffers,
      conversionOfOffersFormatted: formatPercentValue(
        currentConversionOfOffers,
      ),
      conversionOfOffersDelta: conversionDelta.delta,
      conversionOfOffersTrend: conversionDelta.trend,

      avgGuestRating: currentAvgGuestRating,
      avgGuestRatingFormatted: formatRatingValue(currentAvgGuestRating),
      avgGuestRatingDelta: ratingDelta.delta,
      avgGuestRatingTrend: ratingDelta.trend,

      ordersForStay: currentOrdersForStay,
      ordersForStayFormatted: formatOrdersValue(currentOrdersForStay),
      ordersForStayDelta: ordersDelta.delta,
      ordersForStayTrend: ordersDelta.trend,
    };
  });
}
